/**
 * <s-product-image-zoom> — product media viewer and zoom preloader.
 *
 * Thumbnails form an accessible tablist. Selection swaps one primary media
 * panel at a time while image buttons continue to open native zoom dialogs.
 */
class SProductImageZoom extends HTMLElement {
  #ac;
  #preloaded = new Set();
  #preloadLinks = [];
  #reducedMotion;

  connectedCallback() {
    this.#ac = new AbortController();
    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const opts = { signal: this.#ac.signal };

    this.addEventListener('pointerover', this.#handlePreloadIntent, opts);
    this.addEventListener('focusin', this.#handlePreloadIntent, opts);
    this.addEventListener('click', this.#handleThumbnailClick, opts);
    this.addEventListener('keydown', this.#handleThumbnailKeydown, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();

    for (const link of this.#preloadLinks) link.remove();
    this.#preloadLinks = [];
    this.#preloaded.clear();
  }

  #handleThumbnailClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const tab = event.target.closest('[data-product-thumbnail]');
    if (!(tab instanceof HTMLButtonElement) || !this.contains(tab)) return;
    this.#selectTab(tab);
  };

  #handleThumbnailKeydown = (event) => {
    if (!(event.target instanceof HTMLButtonElement) || !event.target.matches('[data-product-thumbnail]')) return;

    const tabs = [...this.querySelectorAll('[data-product-thumbnail]')];
    const currentIndex = tabs.indexOf(event.target);
    if (currentIndex < 0) return;

    let nextIndex;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (currentIndex + 1) % tabs.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextTab = tabs[nextIndex];
    nextTab.focus();
    this.#selectTab(nextTab);
  };

  async #selectTab(tab) {
    if (tab.getAttribute('aria-selected') === 'true') return;

    const tabs = [...this.querySelectorAll('[data-product-thumbnail]')];
    const panels = [...this.querySelectorAll('[data-product-slide]')];
    const nextPanelId = tab.getAttribute('aria-controls');
    const nextPanel = panels.find((panel) => panel.id === nextPanelId);
    const currentPanel = panels.find((panel) => !panel.hidden);
    if (!(nextPanel instanceof HTMLElement)) return;

    for (const item of tabs) {
      const selected = item === tab;
      item.setAttribute('aria-selected', String(selected));
      item.tabIndex = selected ? 0 : -1;
    }

    nextPanel.hidden = false;
    nextPanel.inert = false;

    if (currentPanel && currentPanel !== nextPanel) {
      currentPanel.querySelectorAll('video').forEach((video) => video.pause());

      if (!this.#reducedMotion.matches) {
        const outgoing = currentPanel.animate([{ opacity: 1 }, { opacity: 0 }], {
          duration: 140,
          easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
        });
        nextPanel.animate([{ opacity: 0 }, { opacity: 1 }], {
          duration: 220,
          easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
        });
        await outgoing.finished.catch(() => {});
      }

      currentPanel.hidden = true;
      currentPanel.inert = true;
    }
  }

  #handlePreloadIntent = (event) => {
    if (!(event.target instanceof Element)) return;
    const source = event.target.closest('[data-zoom-preload]');
    if (!source || !this.contains(source)) return;
    this.#preload(source);
  };

  #preload(source) {
    const dialogId = source.getAttribute('commandfor');
    if (!dialogId || this.#preloaded.has(dialogId)) return;

    const dialog = document.getElementById(dialogId);
    const image = dialog?.querySelector('img[data-zoom-image]');
    if (!(image instanceof HTMLImageElement)) return;

    this.#preloaded.add(dialogId);
    image.loading = 'eager';
    image.fetchPriority = 'high';

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = image.src;
    if (image.srcset) link.setAttribute('imagesrcset', image.srcset);
    if (image.sizes) link.setAttribute('imagesizes', image.sizes);
    document.head.append(link);
    this.#preloadLinks.push(link);

    if (typeof image.decode === 'function') image.decode().catch(() => {});
  }
}

if (!customElements.get('s-product-image-zoom')) {
  customElements.define('s-product-image-zoom', SProductImageZoom);
}
